/**
 * @module features/devices/api/credentials-api
 * @description Super Admin credential fetchers + TanStack Query hooks. Responses are
 * Zod-validated and every failure is normalised to an `AppError` (Constitution XIII).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiClient, ApiSuccessEnvelope, AppError } from "@/lib/api-client";
import { DeviceCredentialsData, DeviceCredentialsDataSchema } from "../types/telemetry";

const BASE = "/super-admin/devices";

export const credentialKeys = {
  masked: (id: string) => ["devices", "credentials", id, "masked"] as const,
  all: (id: string) => ["devices", "credentials", id] as const,
};

/** Validate an envelope payload; contract drift becomes a typed AppError. */
export function parseOrThrow<T>(schema: z.ZodType<T>, data: unknown, requestId?: string): T {
  const result = schema.safeParse(data);
  if (!result.success) {
    throw new AppError(
      {
        error_code: "RESPONSE_VALIDATION_ERROR",
        error_message: "The server returned an unexpected response.",
        error_details:
          "The response did not match the expected format. Please try again or contact support.",
        request_id: requestId,
        httpStatus: 502,
      },
      502
    );
  }
  return result.data;
}

/** Fetch credentials; `reveal` asks the server for plaintext values (audited). */
export async function getDeviceCredentials(
  deviceId: string,
  reveal = false
): Promise<DeviceCredentialsData> {
  try {
    const res = await apiClient.get<ApiSuccessEnvelope<unknown>>(
      `${BASE}/${deviceId}/credentials/${reveal ? "?reveal=true" : ""}`
    );
    return parseOrThrow(DeviceCredentialsDataSchema, res.data.data, res.data.request_id);
  } catch (err) {
    throw AppError.fromUnknown(err, "Failed to load device credentials.");
  }
}

/** Rotate both secrets; the response contains the new plaintext pair (shown once). */
export async function rotateDeviceCredentials(
  deviceId: string,
  reason = ""
): Promise<DeviceCredentialsData> {
  try {
    const res = await apiClient.post<ApiSuccessEnvelope<unknown>>(
      `${BASE}/${deviceId}/credentials/rotate/`,
      { reason }
    );
    return parseOrThrow(DeviceCredentialsDataSchema, res.data.data, res.data.request_id);
  } catch (err) {
    throw AppError.fromUnknown(err, "Failed to rotate device credentials.");
  }
}

export function useDeviceCredentials(deviceId: string | null | undefined) {
  return useQuery({
    queryKey: credentialKeys.masked(deviceId ?? ""),
    queryFn: () => getDeviceCredentials(deviceId as string, false),
    enabled: Boolean(deviceId),
  });
}

/** Imperative reveal (plaintext is never cached in the query cache). */
export function useRevealDeviceCredentials() {
  return useMutation({ mutationFn: (deviceId: string) => getDeviceCredentials(deviceId, true) });
}

export function useRotateDeviceCredentials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, reason }: { deviceId: string; reason?: string }) =>
      rotateDeviceCredentials(deviceId, reason ?? ""),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: credentialKeys.all(vars.deviceId) }),
  });
}
